import { match, P } from "ts-pattern";
import { z } from "zod";
import { StableValue } from "../../lib/stable-value";
import { safeParse } from "../../lib/zod";
import { Targets } from "../../introspection";
import {
  ControlDefinition,
  serialize
} from "../definition";
import { DefaultControlInstance } from "../instance";
class Definition extends ControlDefinition {
  constructor(config, version) {
    super(config);
    this.version = version;
  }
  static dataSignature = {
    makeswiftFile: { type: "makeswift-file" },
    externalFile: { type: "external-file" }
  };
  static type = "makeswift::controls::image";
  static Format = {
    URL: `${this.type}::format::url`,
    WithDimensions: `${this.type}::format::with-dimensions`
  };
  static get schema() {
    const type = z.literal(this.type);
    const version = z.literal(1).optional();
    const makeswiftFileValue = z.object({
      type: z.literal(this.dataSignature.makeswiftFile.type),
      id: z.string()
    });
    const externalFileValue = z.object({
      type: z.literal(this.dataSignature.externalFile.type),
      url: z.string(),
      width: z.number().nullable().optional(),
      height: z.number().nullable().optional()
    });
    const makeswiftFileData = makeswiftFileValue.merge(
      z.object({
        version: z.literal(1)
      })
    );
    const externalFileData = externalFileValue.merge(
      z.object({
        version: z.literal(1)
      })
    );
    const data = z.union([
      z.string(),
      z.union([makeswiftFileData, externalFileData])
    ]);
    const value = z.union([makeswiftFileValue, externalFileValue]);
    const config = z.object({
      label: z.string().optional(),
      format: z.union([
        z.literal(this.Format.URL),
        z.literal(this.Format.WithDimensions)
      ]).optional()
    });
    const definition = z.object({
      type,
      config,
      version
    });
    const schemas = (resolvedValue) => ({
      type,
      config,
      definition,
      data,
      value,
      resolvedValue,
      version
    });
    const url = z.string();
    const urlWithDimensions = z.object({
      url,
      dimensions: z.object({
        width: z.number(),
        height: z.number()
      })
    });
    return {
      type,
      version,
      config,
      definition,
      url: schemas(url.optional()),
      withDimensions: schemas(urlWithDimensions.optional())
    };
  }
  static deserialize(data) {
    if (data.type !== Definition.type)
      throw new Error(
        `Image deserialization: expected '${Definition.type}', got '${data.type}'`
      );
    const { config, version } = Definition.schema.definition.parse(data);
    return new ImageDefinition(config, version);
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    const { url, withDimensions, ...baseSchema } = Definition.schema;
    return {
      ...baseSchema,
      value: z.union([url.value, withDimensions.value]),
      data: z.union([url.data, withDimensions.data]),
      resolvedValue: z.union([url.resolvedValue, withDimensions.resolvedValue])
    };
  }
  get refinedSchema() {
    return match(this.config.format).with(Definition.Format.URL, () => Definition.schema.url).with(
      Definition.Format.WithDimensions,
      () => Definition.schema.withDimensions
    ).with(void 0, () => Definition.schema.url).exhaustive();
  }
  get dataSchema() {
    return this.refinedSchema.data;
  }
  safeParse(data) {
    return safeParse(this.dataSchema, data);
  }
  fromData(data) {
    const inputSchema = this.dataSchema.optional();
    return match(data).with(
      Definition.dataSignature.makeswiftFile,
      ({ version, ...value }) => value
    ).with(
      Definition.dataSignature.externalFile,
      ({ version, ...value }) => value
    ).with(P.string, (value) => ({
      ...Definition.dataSignature.makeswiftFile,
      id: value
    })).otherwise(() => void 0);
  }
  toData(value) {
    const version = this.version;
    return match({
      version,
      value
    }).with(
      {
        version: 1,
        value: Definition.dataSignature.makeswiftFile
      },
      ({ value: value2, version: version2 }) => ({ ...value2, version: version2 })
    ).with(
      {
        version: 1,
        value: Definition.dataSignature.externalFile
      },
      ({ value: value2, version: version2 }) => ({ ...value2, version: version2 })
    ).with(
      {
        version: P.nullish,
        value: Definition.dataSignature.makeswiftFile
      },
      ({ value: value2 }) => value2.id
    ).otherwise(() => {
      throw new Error(
        `Invalid ValueType for Image v${this.version ?? 0}: ${JSON.stringify(value)}`
      );
    });
  }
  copyData(data, { replacementContext }) {
    if (data == null)
      return data;
    const replaceFileId = (fileId) => replacementContext.fileIds.get(fileId) ?? fileId;
    const inputSchema = this.dataSchema.optional();
    return match(data).with(P.string, (id) => replaceFileId(id)).with(Definition.dataSignature.makeswiftFile, (data2) => ({
      ...data2,
      id: replaceFileId(data2.id)
    })).otherwise((val) => val);
  }
  resolveValue(data, resolver) {
    const dataSchema = this.dataSchema.optional();
    const externalFile = match(data).with(Definition.dataSignature.externalFile, (val) => val).otherwise(() => null);
    if (externalFile != null) {
      const stableValue2 = StableValue({
        name: Definition.type,
        read: () => this.resolveImage(externalFile)
      });
      return {
        ...stableValue2,
        triggerResolve: async () => {
        }
      };
    }
    const fileId = match(data).with(P.string, (id) => id).with(Definition.dataSignature.makeswiftFile, ({ id }) => id).otherwise(() => void 0);
    const fileSub = resolver.resolveFile(fileId);
    const stableValue = StableValue({
      name: Definition.type,
      read: () => {
        const file = fileSub.readStable();
        return file != null ? this.resolveImage({
          url: file.publicUrl,
          width: file.dimensions?.width,
          height: file.dimensions?.height
        }) : void 0;
      },
      deps: [fileSub]
    });
    return {
      ...stableValue,
      triggerResolve: async (currentValue) => {
        if (currentValue == null) {
          await fileSub.fetch();
        }
      }
    };
  }
  resolveImage({
    url,
    width,
    height
  }) {
    const format = this.config.format;
    if (format === Definition.Format.URL || format == null) {
      return url;
    }
    return width != null && height != null ? {
      url,
      dimensions: { width, height }
    } : void 0;
  }
  createInstance(sendMessage) {
    return new DefaultControlInstance(sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type,
      version: this.version
    });
  }
  introspect(data, target) {
    if (target.type !== Targets.File.type)
      return [];
    const dataSchema = this.dataSchema.optional();
    return match(data).with(P.string, (id) => [id]).with(Definition.dataSignature.makeswiftFile, ({ id }) => [id]).otherwise(() => []);
  }
}
class ImageDefinition extends Definition {
}
function Image(config) {
  return new ImageDefinition(config ?? {}, 1);
}
Image.Format = Definition.Format;
export {
  Image,
  ImageDefinition
};
//# sourceMappingURL=image.js.map